
import TopCategoryItem from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/StatisticsService/TopCategoryItem";
import TopTrackItem from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/StatisticsService/TopTrackItem";
import Vue from "vue";
import { Component } from "vue-property-decorator";

interface DashboardMetric {
  key: string;
  label: string;
  value: string;
  isUnavailable?: boolean;
}

interface DashboardListItem {
  key: string;
  name: string;
  value: string;
}

@Component
export default class PageDashboardComponent extends Vue {
  isLoading = true;
  loadError = "";
  mauCount: number = null;
  dauCount: number = null;
  registrationsDay = 0;
  registrationsWeek = 0;
  registrationsMonth = 0;
  paidUsersCount = 0;
  promoRedemptionsCount = 0;
  topTracks: DashboardListItem[] = [];
  topAffirmations: DashboardListItem[] = [];
  mauUnavailable = false;
  dauUnavailable = false;
  registrationsUnavailable = false;
  paidUsersUnavailable = false;
  promoRedemptionsUnavailable = false;
  topTracksUnavailable = false;
  topAffirmationsUnavailable = false;

  async created() {
    await this.loadDashboard();
  }

  get registrationStats(): Array<{ label: string; value: number }> {
    return [
      { label: "24 часа", value: this.registrationsDay },
      { label: "7 дней", value: this.registrationsWeek },
      { label: "30 дней", value: this.registrationsMonth },
    ];
  }

  get metrics(): DashboardMetric[] {
    return [
      {
        key: "mau",
        label: "Активные пользователи за месяц (MAU)",
        value: this.formatMetricValue(this.mauCount, this.mauUnavailable),
        isUnavailable: this.mauUnavailable,
      },
      {
        key: "dau",
        label: "Активные пользователи за день (DAU)",
        value: this.formatMetricValue(this.dauCount, this.dauUnavailable),
        isUnavailable: this.dauUnavailable,
      },
      {
        key: "registrations",
        label: "Новые регистрации (30 дней)",
        value: this.formatMetricValue(this.registrationsMonth, this.registrationsUnavailable),
        isUnavailable: this.registrationsUnavailable,
      },
      {
        key: "paid-users",
        label: "Платные пользователи",
        value: this.formatMetricValue(this.paidUsersCount, this.paidUsersUnavailable),
        isUnavailable: this.paidUsersUnavailable,
      },
      {
        key: "revenue",
        label: "Выручка (веб)",
        value: "нет данных",
        isUnavailable: true,
      },
      {
        key: "promo",
        label: "Активации промокодов (30 дней)",
        value: this.formatMetricValue(this.promoRedemptionsCount, this.promoRedemptionsUnavailable),
        isUnavailable: this.promoRedemptionsUnavailable,
      },
      {
        key: "tracks",
        label: "Популярные треки",
        value: this.topTracksUnavailable
          ? "нет данных"
          : this.topTracks.length
            ? this.formatNumber(this.topTracks.length)
            : "0",
        isUnavailable: this.topTracksUnavailable,
      },
      {
        key: "affirmations",
        label: "Популярные аффирмации",
        value: this.topAffirmationsUnavailable
          ? "нет данных"
          : this.topAffirmations.length
            ? this.formatNumber(this.topAffirmations.length)
            : "0",
        isUnavailable: this.topAffirmationsUnavailable,
      },
    ];
  }

  async loadDashboard() {
    this.isLoading = true;
    this.loadError = "";
    const dateEnd = new Date();
    const dateStartMonth = this.addDays(dateEnd, -30);

    await Promise.all([
      this.loadMau(),
      this.loadDau(),
      this.loadRegistrations(),
      this.loadPaidUsers(),
      this.loadPromoRedemptions(dateStartMonth, dateEnd),
      this.loadTopTracks(dateStartMonth, dateEnd),
      this.loadTopAffirmations(dateStartMonth, dateEnd),
    ]);

    this.isLoading = false;
  }

  async loadMau() {
    try {
      const response = await this.$api.StatisticsService.getMauAsync();
      this.mauCount = this.readCount(response);
    } catch (error) {
      this.mauUnavailable = true;
      this.appendLoadError("MAU недоступен");
    }
  }

  async loadDau() {
    try {
      const response = await this.$api.StatisticsService.getDauAsync();
      this.dauCount = this.readCount(response);
    } catch (error) {
      this.dauUnavailable = true;
      this.appendLoadError("DAU недоступен");
    }
  }

  async loadRegistrations() {
    const dateEnd = new Date();
    try {
      const day = await this.$api.StatisticsService.getRegistrationsAsync(this.buildDateRangeHours(dateEnd, 24));
      const week = await this.$api.StatisticsService.getRegistrationsAsync(this.buildDateRange(dateEnd, 7));
      const month = await this.$api.StatisticsService.getRegistrationsAsync(this.buildDateRange(dateEnd, 30));

      this.registrationsDay = this.readRegistrationCount(day);
      this.registrationsWeek = this.readRegistrationCount(week);
      this.registrationsMonth = this.readRegistrationCount(month);
    } catch (error) {
      this.registrationsUnavailable = true;
      this.appendLoadError("Статистика регистраций недоступна");
    }
  }

  async loadPaidUsers() {
    try {
      const response = await this.$api.StatisticsService.getPaidUsersAsync({
        SubscriptionStatus: "Active",
        Page: { Skip: 0, Take: 1 },
      });
      this.paidUsersCount = this.readPageCount(response);
    } catch (error) {
      this.paidUsersUnavailable = true;
      this.appendLoadError("Платные пользователи недоступны");
    }
  }

  async loadPromoRedemptions(dateStart: Date, dateEnd: Date) {
    try {
      const response = await this.$api.StatisticsService.getPromoRedemptionsAsync({
        DateStart: dateStart,
        DateEnd: dateEnd,
        Page: { Skip: 0, Take: 1 },
      });
      this.promoRedemptionsCount = this.readPageCount(response);
    } catch (error) {
      this.promoRedemptionsUnavailable = true;
      this.appendLoadError("Активации промокодов недоступны");
    }
  }

  async loadTopTracks(dateStart: Date, dateEnd: Date) {
    try {
      const response = await this.$api.StatisticsService.getTopTracksAsync({
        DateStart: dateStart,
        DateEnd: dateEnd,
        Take: 5,
      });
      const tracks = (response && (response.Tracks || (response as any).tracks)) || [];
      this.topTracks = tracks.slice(0, 5).map((track: TopTrackItem, index: number) => ({
        key: String(track.Id || (track as any).id || index),
        name: track.Name || (track as any).name || `Трек #${index + 1}`,
        value: this.formatNumber(track.ListenCount || (track as any).listenCount || 0),
      }));
    } catch (error) {
      this.topTracksUnavailable = true;
      this.appendLoadError("Популярные треки недоступны");
    }
  }

  async loadTopAffirmations(dateStart: Date, dateEnd: Date) {
    try {
      const response = await this.$api.StatisticsService.getTopAffirmationsAsync({
        DateStart: dateStart,
        DateEnd: dateEnd,
      });
      const items = (response && (response.Items || (response as any).items)) || [];
      this.topAffirmations = items.slice(0, 5).map((item: TopCategoryItem, index: number) => ({
        key: String(item.Id || (item as any).id || index),
        name: item.Name || (item as any).name || `Аффирмация #${index + 1}`,
        value: this.formatNumber(item.ListenCount || (item as any).listenCount || 0),
      }));
    } catch (error) {
      this.topAffirmationsUnavailable = true;
      this.appendLoadError("Популярные аффирмации недоступны");
    }
  }

  buildDateRange(dateEnd: Date, days: number) {
    return {
      DateStart: this.addDays(dateEnd, -days),
      DateEnd: dateEnd,
    };
  }

  buildDateRangeHours(dateEnd: Date, hours: number) {
    return {
      DateStart: new Date(dateEnd.getTime() - hours * 60 * 60 * 1000),
      DateEnd: dateEnd,
    };
  }

  readCount(response: any): number {
    if (response == null) {
      return 0;
    }
    if (typeof response === "number") {
      return response;
    }
    return response.Count != null ? response.Count : (response as any).count || 0;
  }

  readRegistrationCount(response: any): number {
    if (response == null) {
      return 0;
    }
    if (typeof response === "number") {
      return response;
    }
    return response.RegistrationCount != null
      ? response.RegistrationCount
      : (response as any).registrationCount || 0;
  }

  readPageCount(response: any): number {
    if (response == null) {
      return 0;
    }
    if (typeof response === "number") {
      return response;
    }
    const page = response.Page || (response as any).page;
    if (page && (page.Count != null || page.count != null)) {
      return page.Count != null ? page.Count : page.count;
    }
    const items = response.Items || (response as any).items;
    return Array.isArray(items) ? items.length : 0;
  }

  addDays(date: Date, days: number): Date {
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + days);
    return result;
  }

  formatMetricValue(value: number, isUnavailable: boolean): string {
    if (isUnavailable || value === null) {
      return "нет данных";
    }
    return this.formatNumber(value || 0);
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat("ru-RU").format(value || 0);
  }

  appendLoadError(message: string) {
    this.loadError = this.loadError ? `${this.loadError}; ${message}` : message;
  }
}
